import { ReactElement, useCallback } from "react";
import ReactSelect from "react-select";

import Label from "../Label";
import { Control, Controller } from "react-hook-form";
import Hint from "../../fields/Hint";
import classNames from "classnames";
import { get, isString, map } from "lodash";

export function extractValues(values: any[] | any, key = "value"): string[] {
  return map(values, (v) => (isString(v) ? v : v?.[key]));
}

export function extractFirstValue(values: any[] | any, key = "value"): string {
  return extractValues(values, key)?.[0];
}

type SelectArgs = {
  name: string;
  label?: string;
  multi?: boolean;
  required?: boolean | string;
  control?: Control<any>;
  options: {
    key?: string | number;
    value: any;
    label: string | ReactElement;
  }[];
  placeholder?: string;
  isSearchable?: boolean;
  hint?: string;
  isLoading?: boolean;
  error?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
  stringValues?: boolean;
  fromValueToApi?: (value: { label: string; value: string }) => any;
  fromApiToValue?: (value: any) => { label: string; value: string };
};
export default function Select({
  label,
  name,
  multi = false,
  required = false,
  options,
  control,
  placeholder,
  isSearchable = true,
  hint,
  isLoading = false,
  error = false,
  disabled = false,
  isClearable = false,
  stringValues = false,
  fromValueToApi,
  fromApiToValue,
}: SelectArgs) {
  // Helper functions to convert between string values and option objects
  const findOptionByValue = useCallback(
    (searchValue: string) => {
      return options.find((option) => option.value === searchValue);
    },
    [options],
  );

  const findOptionsByValues = useCallback(
    (searchValues: string[]) => {
      return searchValues
        .map((searchValue) => findOptionByValue(searchValue))
        .filter(Boolean);
    },
    [findOptionByValue],
  );

  return (
    <div className="control-group">
      {label && <Label label={label} required={!!required} />}
      <div className="controls remove-input-txt-border">
        <Controller
          name={name}
          control={control}
          rules={{ required: required }}
          render={({ field, formState }) => (
            <>
              <ReactSelect
                closeMenuOnSelect
                isMulti={multi}
                options={options}
                placeholder={placeholder}
                isSearchable={isSearchable}
                noOptionsMessage={() => I18n.t("js.plugins.select2.no_match")}
                value={
                  fromApiToValue
                    ? fromApiToValue(field.value)
                    : stringValues
                      ? multi
                        ? findOptionsByValues(
                            Array.isArray(field.value) ? field.value : [],
                          )
                        : findOptionByValue(field.value || "")
                      : field.value
                }
                onChange={(selectedOption) => {
                  if (fromValueToApi) {
                    field.onChange(fromValueToApi(selectedOption));
                  } else if (stringValues) {
                    // Transform back to string values
                    if (multi) {
                      const stringValues = Array.isArray(selectedOption)
                        ? selectedOption.map((option) => option?.value)
                        : [];
                      field.onChange(stringValues);
                    } else {
                      const stringValue = selectedOption?.value || "";
                      field.onChange(stringValue);
                    }
                  } else {
                    // Default behavior - pass through the object
                    field.onChange(selectedOption);
                  }
                }}
                className={classNames("form-select-container", {
                  "field-error field-error-live":
                    !!get(formState.errors, name) || error,
                })}
                classNamePrefix="form-select"
                isLoading={isLoading}
                isDisabled={disabled}
                isClearable={isClearable || multi}
                unstyled
              />
              {hint && <Hint hint={hint} />}
            </>
          )}
        />
      </div>
    </div>
  );
}
